import { CountrySelectionSchema, countryOption, type CountrySelection, type CurrencyCode, type FinancialFacts, type FinancialPlan, type SupportedCountryCode } from "@moneymap/domain";
import { buildPlan } from "@moneymap/planner";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "./auth/AuthProvider";
import { AuthPanel } from "./components/AuthPanel";
import { AssessmentValue, CompactAssessmentLinks } from "./components/AssessmentValue";
import { BillingPage } from "./components/BillingPage";
import { CountryGate } from "./components/CountryGate";
import { Dashboard } from "./components/Dashboard";
import { PlanView } from "./components/PlanView";
import { QuickCheck } from "./components/QuickCheck";
import { parseAcquisitionAttribution } from "./lib/acquisition";
import { apiRequest } from "./lib/api";

type View = "home" | "check" | "plan" | "dashboard" | "billing";
interface AccountSetup { householdId: string; country: SupportedCountryCode | null; currency: CurrencyCode | null; countrySelected: boolean }
interface CheckoutReturnContext { completed: boolean; email: string }

function checkoutReturnFromLocation(): CheckoutReturnContext {
  const url = new URL(window.location.href);
  if (url.searchParams.get("checkout") !== "complete") return { completed: false, email: "" };

  const value = (url.searchParams.get("email") ?? "").trim().toLowerCase();
  const email = value.length <= 320 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : "";
  let changed = false;
  for (const parameter of ["email", "payment_id", "subscription_id", "license_key", "status"]) {
    if (url.searchParams.has(parameter)) {
      url.searchParams.delete(parameter);
      changed = true;
    }
  }
  if (changed) window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  return { completed: true, email };
}

function countrySelectionFromLocation(): CountrySelection | null {
  const params = new URLSearchParams(window.location.search);
  const parsed = CountrySelectionSchema.safeParse({
    country: params.get("country"),
    currency: params.get("currency"),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  });
  return parsed.success ? parsed.data : null;
}

function writePendingCountry(selection: CountrySelection | null): void {
  const url = new URL(window.location.href);
  if (selection) {
    url.searchParams.set("country", selection.country);
    url.searchParams.set("currency", selection.currency);
  } else {
    url.searchParams.delete("country");
    url.searchParams.delete("currency");
  }
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}

export function App() {
  const auth = useAuth();
  const householdInvite = useRef(new URLSearchParams(window.location.search).get("invite"));
  const checkoutReturn = useRef(checkoutReturnFromLocation());
  const billingIntent = useRef((() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("upgrade") === "pro" || checkoutReturn.current.completed;
  })());
  const checkoutReturned = checkoutReturn.current.completed;
  const [view, setView] = useState<View>(billingIntent.current ? "billing" : "home");
  const [facts, setFacts] = useState<FinancialFacts | null>(null);
  const [checkSeed, setCheckSeed] = useState<FinancialFacts | undefined>();
  const [plan, setPlan] = useState<FinancialPlan | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<CountrySelection | null>(null);
  const [pendingCountry, setPendingCountry] = useState<CountrySelection | null>(() => countrySelectionFromLocation());
  const [accountLoading, setAccountLoading] = useState(false);
  const importKey = useRef(crypto.randomUUID());
  const acquisition = useRef(parseAcquisitionAttribution(window.location.search));

  const signedInSubject = useRef<string | null>(null);

  useEffect(() => {
    const session = auth.session;
    const subject = session?.user.id ?? null;
    if (!session) {
      signedInSubject.current = null;
      setAccount(null);
      return;
    }
    if (signedInSubject.current === subject) return;
    signedInSubject.current = subject;
    let active = true;
    setAccountLoading(true);
    setError(null);
    void (async () => {
      let setup = await apiRequest<AccountSetup>("/api/v1/me/bootstrap", session, {
        method: "POST",
        body: JSON.stringify({ acquisition: acquisition.current })
      });
      acquisition.current = undefined;
      if (householdInvite.current) {
        setup = await apiRequest<AccountSetup>("/api/v1/household/invites/accept", session, {
          method: "POST",
          body: JSON.stringify({ token: householdInvite.current })
        });
        householdInvite.current = null;
        const url = new URL(window.location.href);
        url.searchParams.delete("invite");
        window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
      }
      if (!active) return;
      if (setup.countrySelected && setup.country && setup.currency) {
        const savedSelection = { country: setup.country, currency: setup.currency };
        setAccount(savedSelection);
        setPendingCountry(savedSelection);
        writePendingCountry(savedSelection);
        setView(billingIntent.current ? "billing" : "dashboard");
      } else if (pendingCountry) {
        const selection = await apiRequest<CountrySelection>("/api/v1/me/country", session, {
          method: "PUT",
          body: JSON.stringify(pendingCountry)
        });
        if (!active) return;
        setAccount(selection);
        setView(billingIntent.current ? "billing" : "check");
      } else {
        setAccount(null);
      }
    })().catch((setupError: unknown) => {
      if (active) setError(setupError instanceof Error ? setupError.message : "Your account setup could not be opened.");
    }).finally(() => {
      if (active) setAccountLoading(false);
    });
    return () => { active = false; };
  }, [auth.session, pendingCountry]);

  async function finishCheck(nextFacts: FinancialFacts) {
    const nextPlan = buildPlan(nextFacts);
    setFacts(nextFacts);
    setPlan(nextPlan);
    setSaved(false);
    setError(null);
    importKey.current = crypto.randomUUID();
    setView("plan");
    window.scrollTo({ top: 0 });
    await savePlan(nextFacts);
  }

  function openCheck(seed?: FinancialFacts) {
    setCheckSeed(seed);
    setView("check");
    window.scrollTo({ top: 0 });
  }

  function openIncludedChecks() {
    setView("home");
    window.requestAnimationFrame(() => document.getElementById("included-checks")?.scrollIntoView({ behavior: "smooth" }));
  }

  async function savePlan(nextFacts = facts) {
    if (!auth.session || !nextFacts) return;
    setSaving(true);
    setError(null);
    try {
      await apiRequest("/api/v1/me/bootstrap", auth.session, { method: "POST", body: "{}" });
      const result = await apiRequest<{ plan: FinancialPlan }>("/api/v1/assessments", auth.session, {
        method: "POST",
        body: JSON.stringify({
          facts: nextFacts,
          idempotencyKey: importKey.current
        })
      });
      setPlan(result.plan);
      setSaved(true);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Your plan could not be saved.");
    } finally { setSaving(false); }
  }

  async function handleDeleted() {
    await auth.signOut();
    setFacts(null);
    setPlan(null);
    setSaved(false);
    setView("home");
  }

  if (auth.loading || accountLoading) {
    return <div className="app-shell"><main className="center-panel" id="main-content"><div className="spinner" /><p>Opening MoneyMap securely…</p></main></div>;
  }

  if (!auth.session) {
    return (
      <div className="app-shell signed-out-shell">
        <a className="skip-link" href="#main-content">Skip to content</a>
        <header className="site-header"><div className="page-width nav-inner"><button type="button" className="brand" onClick={() => setView("home")} aria-label="MoneyMap sign in"><span className="brand-mark">M</span><span>MoneyMap<small>Worldwide</small></span></button><nav aria-label="Primary navigation">{!pendingCountry && <a className="nav-link" href="#included-checks">8 connected checks</a>}<a className="nav-link" href="/sample-household/">Sample result</a><a className="nav-link" href="/pricing/">Pricing</a><a className="nav-link" href="/privacy/">Privacy</a></nav></div></header>
        {pendingCountry
          ? <AuthPanel initialEmail={checkoutReturn.current.email} checkoutReturned={checkoutReturned} planningSelection={pendingCountry} onChangeCountry={() => { setPendingCountry(null); writePendingCountry(null); }} />
          : <CountryGate checkoutReturned={checkoutReturned} onSelected={(selection) => { setPendingCountry(selection); writePendingCountry(selection); window.scrollTo({ top: 0 }); }} />}
        <Footer />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="app-shell">
        <a className="skip-link" href="#main-content">Skip to content</a>
        <header className="site-header"><div className="page-width nav-inner"><div className="brand" aria-label="MoneyMap country setup"><span className="brand-mark">M</span><span>MoneyMap<small>Worldwide</small></span></div><nav aria-label="Account navigation"><button className="nav-signin" type="button" onClick={() => { void auth.signOut(); }}>Sign out</button></nav></div></header>
        {error ? <main className="center-panel" id="main-content"><p className="form-error" role="alert">{error}</p><button className="secondary-button" type="button" onClick={() => window.location.reload()}>Try again</button></main> : <CountryGate checkoutReturned={checkoutReturned} session={auth.session} initialSelection={pendingCountry} onSelected={(selection) => { setAccount(selection); setPendingCountry(selection); writePendingCountry(selection); setView(billingIntent.current ? "billing" : "check"); }} />}
        <Footer />
      </div>
    );
  }

  if (view === "billing") {
    return (
      <div className="app-shell">
        <a className="skip-link" href="#main-content">Skip to content</a>
        <header className="site-header"><div className="page-width nav-inner"><button type="button" className="brand" onClick={() => setView("home")} aria-label="MoneyMap home"><span className="brand-mark">M</span><span>MoneyMap<small>Worldwide</small></span></button><nav aria-label="Account navigation"><button className="nav-link" type="button" onClick={() => setView("dashboard")}>My plan</button><button className="nav-signin" type="button" onClick={() => { void auth.signOut(); setView("home"); }}>Sign out</button></nav></div></header>
        <BillingPage session={auth.session} checkoutReturned={checkoutReturned} onDashboard={() => setView("dashboard")} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <header className="site-header"><div className="page-width nav-inner"><button type="button" className="brand" onClick={() => setView("home")} aria-label="MoneyMap home"><span className="brand-mark">M</span><span>MoneyMap<small>Worldwide</small></span></button><nav aria-label="Primary navigation"><button className="nav-link nav-value-link" type="button" onClick={openIncludedChecks}>All 8 checks</button><button className="nav-link" type="button" onClick={() => setView("billing")}>Pro · $25 once</button><a className="nav-link" href="/privacy/">Privacy</a><button className="nav-link" type="button" onClick={() => setView("dashboard")}>Dashboard</button><button className="nav-signin" type="button" onClick={() => { void auth.signOut(); setView("home"); }}>Sign out</button></nav></div></header>

      {view === "home" && <Home selection={account} onStart={() => openCheck()} onDashboard={() => setView("dashboard")} />}
      {view === "check" && <QuickCheck session={auth.session} country={account.country} currency={account.currency} initialFacts={checkSeed} onComplete={finishCheck} onCancel={() => setView("home")} />}
      {view === "plan" && facts && plan && <PlanView session={auth.session} facts={facts} plan={plan} saved={saved} saving={saving} error={error} onSave={() => { void savePlan(); }} onRetake={() => openCheck(facts)} onDashboard={() => setView("dashboard")} onUpgrade={() => setView("billing")} />}
      {view === "dashboard" && <Dashboard session={auth.session} onRetake={(savedFacts) => openCheck(savedFacts)} onHome={() => setView("home")} onUpgrade={() => setView("billing")} onDeleted={handleDeleted} />}
      <Footer />
    </div>
  );
}

function Home({ selection, onStart, onDashboard }: { selection: CountrySelection; onStart: () => void; onDashboard: () => void }) {
  const country = countryOption(selection.country);
  return <main id="main-content"><section className="home-hero"><div className="page-width hero-grid"><div className="hero-copy"><p className="eyebrow">{country.label} household check · {selection.currency}</p><h1>You’ve got savings and protection.<br /><em>But are they enough?</em></h1><p className="hero-lede">Put your household numbers in one place. We’ll test what happens if income stops or a large cost lands, then tell you which gap to deal with first.</p><div className="hero-actions"><button className="primary-button large" data-start-cta type="button" onClick={onStart}>Run all eight checks <span>→</span></button><button className="secondary-button large" type="button" onClick={onDashboard}>Go to my plan</button></div><div className="trust-row"><span>✓ Uses {selection.currency}</span><span>✓ Every calculation is shown</span><span>✓ No product commissions</span></div></div><div className="home-check-board"><div className="home-check-board-heading"><span>INCLUDED IN YOUR ASSESSMENT</span><h2>Eight useful answers. One risk order.</h2><small>Open any calculation to see how it works.</small></div><CompactAssessmentLinks /></div></div></section><AssessmentValue onStart={onStart} /><section className="how page-width"><p className="eyebrow">What you’ll do</p><h2>Seven short steps. One clear starting point.</h2><div className="steps-grid"><article><span>01</span><h3>Add your people</h3><p>Tell us who earns and who depends on that money.</p></article><article><span>02</span><h3>Add the monthly numbers</h3><p>Income, bills, debt payments and cash you could use today.</p></article><article><span>03</span><h3>Test protection</h3><p>We compare reported benefit amounts and public, employer or private coverage—not just whether a policy exists.</p></article><article><span>04</span><h3>Deal with the first gap</h3><p>See the amount, the people affected and a practical next step.</p></article></div></section><section className="global-strip"><div className="page-width"><div><p className="eyebrow">Your selected context</p><h2>{country.label} · {selection.currency}</h2></div><p>{country.packStatus === "reviewed-local" ? "This plan combines the worldwide household engine with reviewed local protection questions." : "This plan uses worldwide household mechanics and keeps unreviewed local healthcare, tax, pension and benefit questions visibly marked for local review."}</p></div></section></main>;
}

function Footer() {
  return <footer><div className="page-width"><span>MoneyMap by AppCradle · see the gap before it becomes a problem</span><div className="footer-links"><a href="/methodology/">How it works</a><a href="/pricing/">Pricing</a><a href="/privacy/">Privacy</a><a href="/terms/">Terms</a><a href="mailto:appcradledevs@gmail.com?subject=MoneyMap%20support">Contact</a><a className="directory-link" href="https://spotstartups.com" target="_blank" rel="noopener noreferrer">Listed on Spot Startups</a><span>Worldwide engine · country-specific packs</span></div></div></footer>;
}
